import React, { useState, useMemo } from 'react';
import { View, LogBox, Dimensions, Modal, ScrollView } from 'react-native';
import { interpolateBlues, schemeCategory10 } from 'd3-scale-chromatic';
import LineChartScreen from '../common/charts/lineChart-builder';
import PaperTableDynamic from '../common/controls/TableProvider';
import PieChartComp from '../common/charts/piChart-builder';
import { getItemById } from '../common/service/dataOperations';
import { useAuth } from '../../../providers/AuthProvider';
import { useClient } from '../../../providers/ClientProvider';
import { scaleOrdinal } from 'd3-scale';
import { Loader } from '../../Common/Loader';
import ToastMessage from '../common/controls/Toast';
import {
  filterData,
  handleFilterReset,
  handleExportClick,
  constructData,
  EnableExport,
  CommonStyles,
  GetConsolidatedLog,
  FilterChoices
} from './ConsolidatedController';
import { FilterIcon, ExportIcon, FilterBox, SelectedFilters } from './ReUsableComps';
import formCustomStyles from '../common/forms/formStyles';
import { TabScreen, Tabs, TabsProvider } from 'react-native-paper-tabs';
import { colorType3, colorType5, logStyles } from '../LogControllers/LogStyles';
import LogKpiRow from '../LogControllers/LogKpiRow';
import LogTypeBreakdown from '../LogControllers/LogTypeBreakdown';
import LogDateRangeFilter from '../common/controls/LogDateRangeFilter';
import processData from '../common/service/dataProcessor';

interface Props {
  siteId: string;
  logTitle: string;
  projectId: string;
  isDashboard?: boolean;
  packageFilter?: string[] | null;
}

import { useQuery } from '@tanstack/react-query';

export default function ConsolWasteWaterLog(props: Props) {
  LogBox.ignoreLogs(["EventEmitter.removeListener"]);

  const { getAccessToken } = useAuth();
  const { clientAPIURL } = useClient();

  const { data: reportData, isLoading } = useQuery({
    queryKey: ['consolidated-log', props.logTitle, props.projectId],
    queryFn: async () => {
      const accessToken = await getAccessToken();
      const jResponse = await GetConsolidatedLog(props.logTitle, clientAPIURL, accessToken, props.projectId);
      return constructData(jResponse);
    },
  });

  if (isLoading) return <Loader />;

  return reportData && reportData.items && reportData.columns ? (
    <CreateLogCharts
      wasteWaterLogData={reportData.items}
      wasteWaterLogColumns={reportData.columns}
      siteId={props.siteId}
      logTitle={props.logTitle}
      clientAPIURL={clientAPIURL}
      isDashboard={props.isDashboard}
      packageFilter={props.packageFilter}
    />
  ) : null;
}

interface CreateLogChartsProps {
  wasteWaterLogData: any;
  wasteWaterLogColumns: any;
  siteId: string;
  logTitle: string;
  clientAPIURL: string;
  isDashboard?: boolean;
  packageFilter?: string[] | null;
}

const CreateLogCharts: React.FC<CreateLogChartsProps> = ({ wasteWaterLogData, wasteWaterLogColumns, siteId, logTitle, clientAPIURL, isDashboard, packageFilter }) => {
  const [FilterVisible, setFilterVisible] = React.useState(false);
  const screenWidth = Dimensions.get('window').width;
  const { getAccessToken } = useAuth();

  const ColumnsWithIDColumn = wasteWaterLogColumns && [
    ...wasteWaterLogColumns,
    {
      id: 'CODE_GENERATED_ID',
      title: 'Item ID',
      columnName: 'ID',
      list: '7ae860e9-6c67-46d0-b65e-f2869542a3aa',
      datatype: 'calculated',
      createdAt: 'YOUR_CREATED_AT_TIMESTAMP',
      modifiedAt: 'YOUR_MODIFIED_AT_TIMESTAMP',
      required: false,
      subType: 'Float',
      choiceChoices: null,
      choiceType: null,
      choiceMultiselect: null,
      multiline: false,
      order: 0,
      calculatedFormula: null,
      listNavigation: null,
    }
  ];

  const updatedData = useMemo(() => {
    const raw = processData(logTitle, wasteWaterLogData);
    if (!packageFilter?.length) return raw;
    return raw.filter((item: any) => packageFilter.includes(item.packageName));
  }, [logTitle, wasteWaterLogData, packageFilter]);

  const [selectedYear, setSelectedYear] = useState<string>('ALL');
  const [selectedMonth, setSelectedMonth] = useState<string>('ALL');
  const [selectedPackage, setSelectedPackage] = useState<string>('ALL');
  const [toastVisibility, setToastVisibility] = useState<boolean>(false);
  const [toastText, setToastText] = useState<string>('');
  const [newData, setNewData] = useState<any[]>(updatedData);
  const [dateRangeFiltered, setDateRangeFiltered] = useState<any[]>(updatedData);

  function returnDate(value: Date) {
    if (value) {
      const date = new Date(value);
      const timezoneOffset = 4 * 60;
      const adjustedDate = new Date(date.getTime() + timezoneOffset * 60000);
      const day = adjustedDate.getDate();
      const month = adjustedDate.getMonth() + 1;
      const year = adjustedDate.getFullYear();
      return `${day < 10 ? '0' + day : day}/${month < 10 ? '0' + month : month}/${year}`;
    } else {
      return value;
    }
  }

  function handleYearFilterSelect(val: string) {
    setSelectedYear(val);
    filterData(val, selectedMonth, selectedPackage, setToastVisibility, updatedData, setToastText, setNewData);
  }
  function handleMonthFilterSelect(val: string) {
    setSelectedMonth(val);
    filterData(selectedYear, val, selectedPackage, setToastVisibility, updatedData, setToastText, setNewData);
  }
  function handlePackageFilterSelect(val: string) {
    filterData(selectedYear, selectedMonth, val, setToastVisibility, updatedData, setToastText, setNewData);
    setSelectedPackage(val);
  }

  async function handleDataUpdate(item: any) {
    if (item.ID) {
      try {
        const accessToken = await getAccessToken();
        const result = await getItemById(logTitle, accessToken, clientAPIURL, siteId, item.ID);
        const updatedDate = result.items[0] && returnDate(result.items[0].Date);
        const newItem = { ...result.items[0], Date: updatedDate };
        setNewData(prev => {
          const without = prev.filter((oItem: any) => oItem.ID !== item.ID);
          return [...without, newItem];
        });
      } catch (ex) {
        console.error(ex);
      }
    }
  }

  function handleToastDismiss() {
    setToastVisibility(false);
  }

  const yearFilterOptions = useMemo(() => FilterChoices([...new Set(updatedData.map((item: any) => item.Year))], 'Year'), [updatedData]);
  const monthFilterOptions = useMemo(() => FilterChoices([...new Set(updatedData.map((item: any) => item.Month))], 'Month'), [updatedData]);
  const packageFilterOptions = useMemo(() => FilterChoices([...new Set(updatedData.map((item: any) => item.packageName))], 'Package'), [updatedData]);

  const chartData = useMemo(() => {
    const colorScale = scaleOrdinal(schemeCategory10);

    const uniquePeriod = [...new Set(dateRangeFiltered.map((item: any) => item.Period))].filter(Boolean) as string[];

    // Items with valid Quantitym3 — for line charts
    const qtyFiltered = dateRangeFiltered.filter((item: any) =>
      item.Quantitym3 != null && item.Quantitym3 !== ''
    );

    // --- Pie 1: WastewaterType / Quantitym3 ---
    const pieAccum1: Record<string, number> = {};
    dateRangeFiltered.forEach((item: any) => {
      if (item.WastewaterType == null) return;
      const key = String(item.WastewaterType);
      pieAccum1[key] = (pieAccum1[key] ?? 0) + (Number(item.Quantitym3) || 0);
    });
    // PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string
    const byWasteWaterTypePieData = Object.entries(pieAccum1).map(([name, value], index) => ({
      name,
      value: Math.round(value),
      color: colorType5(index.toString()),
    }));

    // --- Pie 2: DisposalMethod / Quantitym3 ---
    const pieAccum2: Record<string, number> = {};
    dateRangeFiltered.forEach((item: any) => {
      if (item.DisposalMethod == null) return;
      const key = String(item.DisposalMethod);
      pieAccum2[key] = (pieAccum2[key] ?? 0) + (Number(item.Quantitym3) || 0);
    });
    const byWasteWaterDestinationPieData = Object.entries(pieAccum2).map(([name, value], index) => ({
      name,
      value: Math.round(value),
      color: colorType3(index.toString()),
    }));

    // --- Line 1: WastewaterType monthly Quantitym3 ---
    const uniqueWasteWaterTypes = [...new Set(qtyFiltered.map((item: any) => item.WastewaterType))].filter(Boolean) as string[];
    const periodTypeSums: Record<string, Record<string, number>> = {};
    qtyFiltered.forEach((item: any) => {
      if (!item.Period || item.WastewaterType == null) return;
      const p = String(item.Period);
      const t = String(item.WastewaterType);
      if (!periodTypeSums[p]) periodTypeSums[p] = {};
      periodTypeSums[p][t] = (periodTypeSums[p][t] ?? 0) + (Number(item.Quantitym3) || 0);
    });
    // LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string
    const lineDataByType = {
      labels: uniquePeriod,
      datasets: uniqueWasteWaterTypes.map((type, index) => ({
        data: uniquePeriod.map(p => Math.round(periodTypeSums[p]?.[type] ?? 0)),
        strokeWidth: 2,
        color: (_opacity = 1) => colorScale(String(index)),
      })),
      legend: uniqueWasteWaterTypes.map(type => ({ name: type })),
    };

    // --- Line 2: DisposalMethod monthly Quantitym3 ---
    const uniqueDisposalMethods = [...new Set(qtyFiltered.map((item: any) => item.DisposalMethod))].filter(Boolean) as string[];
    const periodMethodSums: Record<string, Record<string, number>> = {};
    qtyFiltered.forEach((item: any) => {
      if (!item.Period || item.DisposalMethod == null) return;
      const p = String(item.Period);
      const m = String(item.DisposalMethod);
      if (!periodMethodSums[p]) periodMethodSums[p] = {};
      periodMethodSums[p][m] = (periodMethodSums[p][m] ?? 0) + (Number(item.Quantitym3) || 0);
    });
    const lineDataByDisposal = {
      labels: uniquePeriod,
      datasets: uniqueDisposalMethods.map((method, index) => ({
        data: uniquePeriod.map(p => Math.round(periodMethodSums[p]?.[method] ?? 0)),
        strokeWidth: 2,
        color: (_opacity = 1) => colorScale(String(uniqueWasteWaterTypes.length + index)),
      })),
      legend: uniqueDisposalMethods.map(method => ({ name: method })),
    };

    // --- KPI ---
    const totalCount = dateRangeFiltered.reduce((s: number, item: any) => s + (Number(item.Quantitym3) || 0), 0);
    const typeKpiItems = Object.entries(pieAccum1).map(([name, value], index) => ({
      label: name,
      value: Math.round(value),
      color: colorType5(index.toString()),
      unit: 'm³',
    }));

    return {
      byWasteWaterTypePieData,
      byWasteWaterDestinationPieData,
      lineDataByType,
      lineDataByDisposal,
      totalCount,
      typeKpiItems,
    };
  }, [dateRangeFiltered, wasteWaterLogColumns]);

  const kpiItems = [
    { label: 'Total Volume (m3)', value: Math.round(chartData.totalCount), color: '#00695C' },
    { label: 'Entries', value: dateRangeFiltered.length, color: '#4527A0' },
    ...chartData.typeKpiItems,
  ];

  return (
    <View style={logStyles.logRoot}>
      <View style={CommonStyles.ribbonIconContainer}>
        <View style={CommonStyles.IconContainer}>
          <FilterIcon setFilterVisible={setFilterVisible} FilterVisible={FilterVisible} />
        </View>
        <View style={CommonStyles.IconContainer}>
          {EnableExport() === true
            ? <ExportIcon handleExportClick={handleExportClick} newData={newData} />
            : null
          }
        </View>
        <SelectedFilters selectedMonth={selectedMonth} selectedPackage={selectedPackage} selectedYear={selectedYear} />
      </View>
      <LogTypeBreakdown data={dateRangeFiltered} columns={wasteWaterLogColumns} typeField="DisposalMethod" valueField="Quantitym3" unit="m³" />
      <Modal
        animationType="fade"
        transparent={true}
        visible={FilterVisible}
        onRequestClose={() => { setFilterVisible(!FilterVisible); }}>
        <View style={formCustomStyles.centeredView}>
          <FilterBox
            FilterVisible={FilterVisible}
            setFilterVisible={setFilterVisible}
            screenWidth={screenWidth}
            CommonStyles={CommonStyles}
            yearFilterOptions={yearFilterOptions}
            handleYearFilterSelect={handleYearFilterSelect}
            selectedYear={selectedYear}
            monthFilterOptions={monthFilterOptions}
            handleMonthFilterSelect={handleMonthFilterSelect}
            selectedMonth={selectedMonth}
            packageFilterOptions={packageFilterOptions}
            handlePackageFilterSelect={handlePackageFilterSelect}
            selectedPackage={selectedPackage}
            handleFilterReset={handleFilterReset}
            setSelectedMonth={setSelectedMonth}
            setSelectedYear={setSelectedYear}
            setSelectedPackage={setSelectedPackage}
            setNewData={setNewData}
            updatedData={updatedData}
          />
        </View>
      </Modal>
      <View>
        <ToastMessage
          show={toastVisibility}
          actionLabel="Dismiss"
          toastMessage={toastText}
          duration={3000}
          onDismiss={handleToastDismiss}
        />
      </View>

      {isDashboard ? (
        <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
          <View style={{ zIndex: 200 }}>
            <LogKpiRow items={kpiItems}>
              <LogDateRangeFilter data={newData} onFilterChange={setDateRangeFiltered} />
            </LogKpiRow>
          </View>
          <View>
            <View style={CommonStyles.container}>
              <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                {/* PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string */}
                <PieChartComp data={chartData.byWasteWaterTypePieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - m3)'} ChartTitle={"Qty / Type"} />
              </View>
              <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                {/* LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string */}
                <LineChartScreen legend={chartData.lineDataByType.legend} data={chartData.lineDataByType} label={chartData.lineDataByType.labels} subTitle={'(Monthly - m3)'} ChartTitle={"Qty / Type"} />
              </View>
              <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                <PieChartComp data={chartData.byWasteWaterDestinationPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - m3)'} ChartTitle={"Qty / Destination"} />
              </View>
              <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                <LineChartScreen legend={chartData.lineDataByDisposal.legend} data={chartData.lineDataByDisposal} label={chartData.lineDataByDisposal.labels} subTitle={'(Monthly - m3)'} ChartTitle={"Qty / Destination"} />
              </View>
            </View>
          </View>
        </ScrollView>
      ) : (
        <TabsProvider defaultIndex={0}>
          <Tabs style={{ backgroundColor: '#fff', height: "100%" }} showLeadingSpace={true}>
            <TabScreen label="Charts" icon="chart-bar">
              <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
                <View style={{ zIndex: 200 }}>
                  <LogKpiRow items={kpiItems}>
                    <LogDateRangeFilter data={newData} onFilterChange={setDateRangeFiltered} />
                  </LogKpiRow>
                </View>
                <View>
                  <View style={CommonStyles.container}>
                    <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                      <PieChartComp data={chartData.byWasteWaterTypePieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - m3)'} ChartTitle={"Qty / Type"} />
                    </View>
                    <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                      <LineChartScreen legend={chartData.lineDataByType.legend} data={chartData.lineDataByType} label={chartData.lineDataByType.labels} subTitle={'(Monthly - m3)'} ChartTitle={"Qty / Type"} />
                    </View>
                    <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                      <PieChartComp data={chartData.byWasteWaterDestinationPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - m3)'} ChartTitle={"Qty / Destination"} />
                    </View>
                    <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                      <LineChartScreen legend={chartData.lineDataByDisposal.legend} data={chartData.lineDataByDisposal} label={chartData.lineDataByDisposal.labels} subTitle={'(Monthly - m3)'} ChartTitle={"Qty / Destination"} />
                    </View>
                  </View>
                </View>
              </ScrollView>
            </TabScreen>
            <TabScreen label="List" icon="database">
              <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
                <View>
                  <View style={CommonStyles.container}>
                    <View style={CommonStyles.cardThree}>
                      {dateRangeFiltered && dateRangeFiltered.length > 0 && ColumnsWithIDColumn && (
                        <PaperTableDynamic
                          data={dateRangeFiltered}
                          columns={ColumnsWithIDColumn}
                          siteId={siteId}
                          logTitle={logTitle}
                          updateData={handleDataUpdate}
                          isConsolidatedTable={true}
                        />
                      )}
                    </View>
                  </View>
                </View>
              </ScrollView>
            </TabScreen>
          </Tabs>
        </TabsProvider>
      )}
    </View>
  );
};
